// Lead list service. Two read shapes (page payload + status counts)
// and three writes (add items, change item status, bulk push).
// Bulk push reuses close-push under the hood with source_kind='lead_list'.

import { authHeaders } from '../../lib/authHeaders';

export type LeadListStatus = 'pending' | 'researched' | 'pushed' | 'dropped';

export interface LeadListItem {
  id: string;
  party_id: string;
  display_name: string;
  display_name_natural: string | null;
  normalized_name: string;
  entity_type: string;
  has_phone: boolean;
  has_address: boolean;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  status: LeadListStatus;
  pushed_at: string | null;
  added_at: string;
  added_by: string;
  notes: string | null;
  /** Most-recent doc number this party is a grantor on (any TRS), or
   *  null if the party has no recorder docs. Drives the bulk
   *  "Copy N doc numbers" action on the lead list. */
  most_recent_doc_number: string | null;
  /** Operator-entered city for EnformionGO. Preserved verbatim. NULL
   *  when not yet captured. */
  skip_trace_city: string | null;
  /** Operator-entered US 2-char state code. Uppercase. */
  skip_trace_state: string | null;
  /** When the city/state pair was last written via PATCH. */
  skip_trace_location_set_at: string | null;
  /** True when Enformion datesOfDeath[] was non-empty or
   *  indicators.HasDeathRecords was true on the matched person. */
  deceased: boolean;
  /** True when the last skip-trace returned counts.searchResults > 1
   *  or searchResultsOverflow — the operator accepted the first
   *  result but should eyeball before push. */
  needs_review: boolean;
  /** Timestamp of the most recent skip-trace attempt (matched OR
   *  no-match). NULL means never traced. */
  skip_traced_at: string | null;
  /** The Enformion tahoe_id of the currently-selected candidate (the
   *  person whose phones we merged in). NULL means never traced or
   *  zero matches. */
  enformion_tahoe_id: string | null;
  /** Slim snapshot of the candidates returned on the most recent
   *  trace. Powers the ⚠ click-to-review flow. NULL means never
   *  traced (or traced pre-023). */
  enformion_candidates: SlimCandidate[] | null;
}

/** Slim candidate stored in mi.parties.enformion_candidates jsonb.
 *  Shape mirrors functions/_lib/mi/skip-trace-merge.ts SlimCandidate. */
export interface SlimCandidate {
  tahoeId: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  age: number | null;
  addresses: Array<{
    line1: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    isCurrent: boolean | null;
    lastReportedDate: string | null;
  }>;
  phoneNumbers: Array<{
    phoneNumber: string;
    phoneType: string | null;
    isConnected: boolean | null;
    lastReportedDate: string | null;
  }>;
  deceased: boolean;
}

export type LeadListSourceKind = 'aoi' | 'pool_order' | 'wy_aoi' | 'wy_order';

export interface LeadListGroup {
  source_kind: LeadListSourceKind;
  source_id: string;
  source_label: string;
  items: LeadListItem[];
}

export interface LeadListResponse {
  groups: LeadListGroup[];
  counts: Record<LeadListStatus, number>;
  selected_statuses: LeadListStatus[];
  jurisdiction: 'CO' | 'WY';
  hide_skipped: boolean;
  needs_location: boolean;
  skip_ready:  boolean;
  skip_traced: boolean;
  skipped_party_count: number;
  skip_trace: {
    /** Total visible items inside the status+hide_skipped scope (NOT
     *  inside the needs_location / skip_ready / skip_traced sub-
     *  filters). */
    total: number;
    /** Items where both skip_trace_city and skip_trace_state are set. */
    ready: number;
    /** Items where at least one of city/state is NULL. */
    needs_location: number;
    /** Items where parties.skip_traced_at IS NOT NULL. */
    traced: number;
  };
  total_visible: number;
}

export interface AddItemsResponse {
  requested: number;
  added: number;
  already_in_list: number;
  inserted_items: Array<{ id: string; party_id: string }>;
}

// ── CSV skip-trace (Cowork recorder grantor addresses) ───────────────
// Mirrors functions/_lib/mi/skip-trace-csv.ts. Two calls: upload (dry
// run → bucketed review queue) then commit (operator's resolved queue).
export type CsvRowBucket = 'auto' | 'review' | 'unknown';
export type CsvReviewReason =
  | 'joint_grantor' | 'doc_name_mismatch' | 'ambiguous' | 'fuzzy_review' | 'kind_mismatch';
export type CsvMatchTier = 'doc' | 'name_exact' | 'fuzzy';

export interface CsvRowData {
  doc_number: string; first: string; last: string; entity: string;
  address: string; city: string; state: string; zip: string;
}

export interface CsvMatchCandidate {
  party_id: string;
  display_name: string;
  display_name_natural: string | null;
  normalized_name: string;
  entity_type: string;
  sim: number | null;
  has_phone: boolean;
  has_address: boolean;
}

export interface CsvUploadRow {
  row_index: number;
  data: CsvRowData;
  is_entity: boolean;
  is_joint: boolean;
  is_po_box: boolean;
  bucket: CsvRowBucket;
  matched?: CsvMatchCandidate;
  match_tier?: CsvMatchTier;
  would_trace?: boolean;
  review_reason?: CsvReviewReason;
  candidates?: CsvMatchCandidate[];
  grantor_names?: string[];
  split_halves?: Array<{ first: string; last: string }>;
  doc_found?: boolean;
}

export interface CsvUploadAggregates {
  total_rows: number;
  auto_attached: number;
  needs_review: number;
  unknown: number;
  parties_with_existing_phone: number;
  parties_with_existing_address: number;
  parties_to_trace_default: number;
}

export interface CsvUploadResponse {
  rows: CsvUploadRow[];
  aggregates: CsvUploadAggregates;
  warnings: string[];
}

export type CsvCommitAction = 'attach' | 'add_new' | 'drop';
export interface CsvCommitRow {
  data: CsvRowData;
  action: CsvCommitAction;
  party_id?: string;
  trace: boolean;
  is_entity?: boolean;
}

export type CsvTraceStatus =
  | 'matched' | 'no-match' | 'skipped' | 'entity-skipped' | 'not-traced' | 'error';

export interface CsvCommitRowOutcome {
  data: CsvRowData;
  action: CsvCommitAction;
  party_id: string | null;
  address_written: boolean;
  address_duplicate: boolean;
  traced: boolean;
  trace_status: CsvTraceStatus;
  inserted_phones?: number;
  promoted_phone?: string | null;
  inserted_emails?: number;
  promoted_email?: string | null;
  deceased?: boolean;
  is_po_box: boolean;
  message?: string;
}

export interface CsvCommitResponse {
  outcomes: CsvCommitRowOutcome[];
  addresses_written: number;
  parties_created: number;
  traced: number;
  matched: number;
  no_match: number;
  errors: number;
}

async function jsonReq<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const leadListApi = {
  get: (
    statuses: LeadListStatus[] = ['pending', 'researched'],
    opts: {
      hideSkipped?: boolean;
      needsLocation?: boolean;
      skipReady?: boolean;
      skipTraced?: boolean;
      jurisdiction?: 'CO' | 'WY';
    } = {},
  ) => {
    const params = new URLSearchParams({ status: statuses.join(',') });
    if (opts.hideSkipped)   params.set('hide_skipped',   'true');
    if (opts.needsLocation) params.set('needs_location', 'true');
    if (opts.skipReady)     params.set('skip_ready',     'true');
    if (opts.skipTraced)    params.set('skip_traced',    'true');
    // Only emit the param when WY; CO is the default and the URL
    // stays clean for the existing Weld flow.
    if (opts.jurisdiction === 'WY') params.set('jurisdiction', 'WY');
    return jsonReq<LeadListResponse>(`/api/mi/lead-list?${params.toString()}`);
  },

  addItems: (body: { party_ids: string[]; source_kind: LeadListSourceKind; source_id: string; notes?: string }) =>
    jsonReq<AddItemsResponse>(`/api/mi/lead-list/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  setStatus: (itemId: string, status: Exclude<LeadListStatus, 'pushed'>) =>
    jsonReq<{ item_id: string; status: LeadListStatus }>(`/api/mi/lead-list/items/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }),

  // Bulk Enformion Person Search trace. Sequential server-side
  // (CONCURRENCY=1 in the handler) so the operator can read per-row
  // outcomes as a batch result, then refresh the lead list to see
  // the new phones/deceased/needs_review flags.
  skipTrace: (partyIds: string[]) =>
    jsonReq<{
      requested: number;
      matched: number;
      needs_review: number;
      no_match: number;
      errors: number;
      skipped: number;
      outcomes: Array<{
        party_id: string;
        status: 'matched' | 'needs-review' | 'no-match' | 'error' | 'skipped';
        message?: string;
        match_count?: number;
        inserted_phones?: number;
        promoted_phone?: string | null;
        deceased?: boolean;
        needs_review?: boolean;
        tahoe_id?: string | null;
      }>;
    }>(`/api/mi/lead-list/skip-trace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ party_ids: partyIds }),
    }),

  // CSV skip-trace DRY RUN. Multipart upload; no Content-Type header so
  // the browser sets the multipart boundary. Returns the bucketed review
  // queue + aggregates. Zero DB writes.
  uploadSkipTraceCsv: (file: File, jurisdiction: 'CO' | 'WY' = 'CO') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('jurisdiction', jurisdiction);
    return jsonReq<CsvUploadResponse>(`/api/mi/lead-list/upload-skip-trace-csv`, {
      method: 'POST',
      body: fd,
    });
  },

  // CSV skip-trace COMMIT. The operator's resolved review queue. Writes
  // addresses + fires Contact Enrich for the trace subset.
  commitSkipTraceCsv: (rows: CsvCommitRow[]) =>
    jsonReq<CsvCommitResponse>(`/api/mi/lead-list/skip-trace-csv-commit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows }),
    }),

  // Manual recovery: assert that these items already landed in Close.
  // Flips status to 'pushed' and stamps the parties' last_pushed_to_
  // close_at. Used when a previous close-push call 502'd after Close
  // created the leads but before our DB writes finished.
  markPushedManual: (itemIds: string[]) =>
    jsonReq<{ requested: number; items_flipped: number; parties_stamped: number; parties_already_stamped: number }>(
      `/api/mi/lead-list/mark-pushed-manual`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_ids: itemIds }) }
    ),
};
